import * as React from 'react';
import {
  Box,
  IconButton,
  MenuItem,
  Typography,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import type { GridColumns } from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import {
  useNavigate,
} from 'react-router-dom';
import type { IRule } from '../../shared/Utilities/PolicyConfiguratorInterfaces';
import {
  companyRequiredWorkflowTypes,
  POLICYCONFIGURATORSECTIONS,
} from '../../shared/Utilities/PolicyConfiguratorInterfaces';
import {
  getPricingConfiguratorSectionNameFromWorkflow,
  getWorkflowFromWorkflowName,
} from '../../shared/Utilities/PolicyConfiguratorHelpers';
import ActionMenu from '../../../global/ActionMenu';
import globalUseStyles from '../../../../globalClasses';

interface IRuleResultsProps {
  workflowName: string
  data: IRule[],
  loading: boolean,
  handleDeleteRule: (ruleName: string | undefined) => void;
  currentPage: number,
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}

const RuleResults: React.FC<IRuleResultsProps> = ({
  workflowName, data, loading, handleDeleteRule, currentPage, setCurrentPage,
}) => {
  // Initial imports
  const globalStyles = globalUseStyles();
  const navigate = useNavigate();

  // State hooks for dynamic component elements
  const [selectedRow, setSelectedRow] = React.useState<IRule | undefined>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleVertIconClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!anchorEl) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDeleteRuleButton = () => {
    handleDeleteRule(selectedRow?.ruleName);
    handleCloseMenu();
  };

  // Component configuration based on props
  const companyRequired = companyRequiredWorkflowTypes.includes(workflowName);

  const workflowEnum = getWorkflowFromWorkflowName(workflowName);
  const pricingConfiguratorSectionName = getPricingConfiguratorSectionNameFromWorkflow(workflowEnum);
  const editRuleUrl = `/edit-${pricingConfiguratorSectionName.replace(/\s/g, '')}-rule?ruleName=${encodeURIComponent(selectedRow?.ruleName || '')}&workflow=${workflowName}&isEdit=true`;
  const cloneRuleUrl = `/create-${pricingConfiguratorSectionName.replace(/\s/g, '')}-rule?ruleName=${encodeURIComponent(selectedRow?.ruleName || '')}&workflow=${workflowName}`;

  // Datagrid configuration
  const tableColumns: GridColumns = [
    {
      field: 'company', headerName: 'Company', flex: 1, hide: !companyRequired,
    },
    {
      field: 'collectionOperation', headerName: 'Collection', flex: 1, hide: pricingConfiguratorSectionName !== POLICYCONFIGURATORSECTIONS.ROUTING,
    },
    { field: 'field', headerName: 'Field', flex: 1 },
    { field: 'operation', headerName: 'Operation', flex: 1 },
    { field: 'value', headerName: 'Value', flex: 1 },
    { field: 'condition', headerName: 'Condition', flex: 1 },
    { field: 'operator', headerName: 'Operator', flex: 1 },
    { field: 'successEvent', headerName: 'Value', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.75,
      renderCell: () => (
        <IconButton
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleVertIconClick(event)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const tableRows = data && data?.map((rule) => ({
    id: rule.ruleName || '',
    company: rule.companyName || '',
    field: rule.conditionField || '',
    operation: rule.conditionOperator || '',
    collectionOperation: rule.conditionCollectionOperator || '',
    value: rule.conditionValue || '',
    condition: rule.outcomeField || '',
    operator: rule.outcomeOperator || '',
    successEvent: rule.outcomeValue || '',
  }));

  // This will calculate the height of the RuleResults gridview dynamically.
  const caculateTableHeight = (): number => {
    let totalHeight = 300;
    const rowsPerPage = 5; // Number of table rows per page

    if (tableRows.length) {
      const lastPageRowCount = tableRows.length % rowsPerPage; // Number of rows in the last page

      // Calculate the starting index and ending index of the table rows for the given page
      const startIndex = (currentPage - 1) * rowsPerPage;
      let endIndex = currentPage === Math.ceil(tableRows.length / rowsPerPage)
        ? startIndex + lastPageRowCount
        : startIndex + rowsPerPage;

      if (lastPageRowCount === 0) {
        endIndex = startIndex + rowsPerPage;
      }

      // Iterate through the relevant table rows for the given page
      // eslint-disable-next-line no-plusplus
      for (let i = startIndex; i < endIndex; i++) {
        const fields = tableRows[i]?.field?.split('\n')?.filter((field) => field?.trim() !== '');
        if (fields) {
          totalHeight += fields.length > 1 ? 60 + fields.length * 12 : 70;
        }
      }
    }
    return totalHeight;
  };

  // This will save the page number value which will then be used for calculating the height of the gridview.
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber + 1);
  };

  // Render
  return (
    <>
      <Box py={1}>
        <Typography variant="h6">
          {`${workflowName} Rules`}
        </Typography>
      </Box>
      <Box py={1} />
      <Box height={`${caculateTableHeight()}px`}>
        <DataGrid
          onPageChange={handlePageChange}
          rows={tableRows || []}
          columns={tableColumns}
          pageSize={5}
          loading={loading}
          onSelectionModelChange={(sr) => {
            const row = data?.find((w) => w.ruleName === sr[0]);
            if (row) {
              setSelectedRow(row);
            }
          }}
          className={globalStyles.rulesDataGrid}
        />
        {anchorEl
          && (
            <ActionMenu
              anchorEl={anchorEl}
              handleClose={handleCloseMenu}
            >
              <MenuItem
                id="edit-rule-button"
                onClick={() => navigate(editRuleUrl)}
              >
                {'Edit'}
              </MenuItem>
              <MenuItem
                id="delete-rule-button"
                onClick={handleDeleteRuleButton}
              >
                {'Delete'}
              </MenuItem>
              <MenuItem
                id="clone-rule-button"
                onClick={() => navigate(cloneRuleUrl)}
              >
                {'Clone as New'}
              </MenuItem>
            </ActionMenu>
          )}
      </Box>
    </>
  );
};

export default RuleResults;
